import React, { useState, useEffect } from 'react';
import { getDatabase, ref, push, onValue, off, update, remove } from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Assuming firebase is initialized in a separate file
import { database, auth } from '../firebase';

function DailyExpensePage() {
  const [expense, setExpense] = useState({ type: '', amount: '', gst: '', gstAmount: '', method: '' });
  const [expenses, setExpenses] = useState([]);
  const [balance, setBalance] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportFilter, setReportFilter] = useState('all'); // 'all', 'monthly', 'yearly'
  const [editingExpenseId, setEditingExpenseId] = useState(null); // Track the expense being edited

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setExpenses([]);
        setBalance(0);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const expensesRef = ref(database, `users/${encodedEmail}/expenses`);

    const fetchExpenses = () => {
      onValue(expensesRef, (snapshot) => {
        const data = snapshot.val();
        const expenseList = data
          ? Object.entries(data).map(([id, value]) => ({ id, ...value }))
          : [];
        setExpenses(expenseList);
        setBalance(expenseList.reduce((acc, curr) => acc - ((Number(curr.amount) || 0) + (Number(curr.gstAmount) || 0)), 0));
        setLoading(false);
      }, (error) => {
        console.error('Error fetching expenses: ', error);
        setError('Failed to load expenses. Please try again.');
        setLoading(false);
      });
    };

    fetchExpenses();

    return () => {
      off(expensesRef);
    };
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setExpense((prev) => {
      const updatedExpense = { ...prev, [name]: value };
      if (name === 'amount' || name === 'gst') {
        const amount = Number(updatedExpense.amount) || 0;
        const gst = Number(updatedExpense.gst) || 0;
        updatedExpense.gstAmount = ((amount * gst) / 100).toFixed(2);
      }
      return updatedExpense;
    });
  };

  const addOrUpdateExpense = () => {
    if (!currentUser) {
      setError('You must be logged in to add or update expenses.');
      return;
    }

    if (!expense.type || !expense.amount || !expense.method) {
      setError('Please fill in all required fields.');
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const expensesRef = ref(database, `users/${encodedEmail}/expenses`);

    const expenseData = {
      ...expense,
      amount: Number(expense.amount),
      gst: expense.gst ? Number(expense.gst) : 0,
      gstAmount: Number(expense.gstAmount) || 0,
      createdAt: editingExpenseId ? expenses.find(exp => exp.id === editingExpenseId).createdAt : Date.now()
    };

    if (editingExpenseId) {
      // Update existing expense
      const expenseRef = ref(database, `users/${encodedEmail}/expenses/${editingExpenseId}`);
      update(expenseRef, expenseData)
        .then(() => {
          setExpense({ type: '', amount: '', gst: '', gstAmount: '', method: '' });
          setEditingExpenseId(null);
          setError(null);
        })
        .catch((error) => {
          console.error('Error updating expense: ', error);
          setError('Failed to update expense. Please try again.');
        });
    } else {
      // Add new expense
      push(expensesRef, expenseData)
        .then(() => {
          setExpense({ type: '', amount: '', gst: '', gstAmount: '', method: '' });
          setError(null);
        })
        .catch((error) => {
          console.error('Error adding expense: ', error);
          setError('Failed to add expense. Please try again.');
        });
    }
  };

  const editExpense = (expense) => {
    setExpense({
      type: expense.type,
      amount: expense.amount.toString(),
      gst: expense.gst ? expense.gst.toString() : '',
      gstAmount: expense.gstAmount ? expense.gstAmount.toString() : '',
      method: expense.method
    });
    setEditingExpenseId(expense.id);
    setError(null);
  };

  const deleteExpense = (expenseId) => {
    if (!currentUser) {
      setError('You must be logged in to delete expenses.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const expenseRef = ref(database, `users/${encodedEmail}/expenses/${expenseId}`);

    remove(expenseRef)
      .then(() => {
        setError(null);
      })
      .catch((error) => {
        console.error('Error deleting expense: ', error);
        setError('Failed to delete expense. Please try again.');
      });
  };

  const cancelEdit = () => {
    setExpense({ type: '', amount: '', gst: '', gstAmount: '', method: '' });
    setEditingExpenseId(null);
    setError(null);
  };

  const filterExpensesByDate = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    if (reportFilter === 'monthly') {
      return expenses.filter(exp => {
        const expDate = new Date(exp.createdAt);
        return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
      });
    } else if (reportFilter === 'yearly') {
      return expenses.filter(exp => {
        const expDate = new Date(exp.createdAt);
        return expDate.getFullYear() === currentYear;
      });
    }
    return expenses;
  };

  const filteredExpenses = filterExpensesByDate();
  const filteredBalance = filteredExpenses.reduce((acc, curr) => acc - ((Number(curr.amount) || 0) + (Number(curr.gstAmount) || 0)), 0);

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    reportFilterContainer: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    filterButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      border: '2px solid #e5e7eb',
      backgroundColor: 'white',
      color: '#374151',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '0.875rem',
      transition: 'all 0.2s ease',
    },
    filterButtonActive: {
      backgroundColor: '#2563eb',
      color: 'white',
      borderColor: '#2563eb',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'left',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'left',
    },
    actionCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      textAlign: 'center',
      verticalAlign: 'middle',
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
      textAlign: 'center',
      width: '60px',
    },
    formContainer: {
      padding: '2rem',
      maxWidth: '32rem',
      margin: '0 auto',
    },
    formLabel: {
      display: 'block',
      color: '#374151',
      fontWeight: 'bold',
      fontSize: '1.125rem',
      marginBottom: '0.75rem',
    },
    formInput: {
      width: '100%',
      padding: '1rem 1.5rem',
      fontSize: '1.125rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.75rem',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      boxSizing: 'border-box',
    },
    formInputDisabled: {
      width: '100%',
      padding: '1rem 1.5rem',
      fontSize: '1.125rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.75rem',
      outline: 'none',
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      boxSizing: 'border-box',
    },
    formSelect: {
      width: '100%',
      padding: '1rem 1.5rem',
      fontSize: '1.125rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.75rem',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      boxSizing: 'border-box',
      backgroundColor: 'white',
    },
    addButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    cancelButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#6b7280',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(107, 114, 128, 0.3)',
      transform: 'translateY(0)',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '0.85rem',
      transition: 'all 0.2s ease',
      border: 'none',
    },
    editButton: {
      backgroundColor: '#10b981',
      color: 'white',
    },
    deleteButton: {
      backgroundColor: '#ef4444',
      color: 'white',
    },
    errorMessage: {
      marginTop: '1.5rem',
      padding: '1rem',
      borderRadius: '0.75rem',
      fontWeight: '500',
      fontSize: '1rem',
      backgroundColor: '#fef2f2',
      color: '#b91c1c',
      border: '2px solid #fecaca',
      textAlign: 'center',
    },
    balanceContainer: {
      marginTop: '2rem',
      textAlign: 'center',
      fontSize: '1.25rem',
      color: '#2d3748',
      fontWeight: '500',
      padding: '1rem',
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Daily Expense</h1>
      </div>

      <div style={styles.tableContainer}>
        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}
        {loading ? (
          <div style={styles.noData}>
            <p>⏳ Loading expenses...</p>
          </div>
        ) : !currentUser ? (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>🔐</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>Daily Expenses</h3>
            <p>Please log in to manage expenses.</p>
          </div>
        ) : (
          <>
            <div style={styles.formContainer}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#2563eb',
                marginBottom: '1rem'
              }}>
                {editingExpenseId ? 'Edit Expense' : 'Add New Expense'}
              </h2>
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={styles.formLabel}>Expense Type *</label>
                  <input
                    type="text"
                    name="type"
                    placeholder="Enter expense type"
                    value={expense.type}
                    onChange={handleChange}
                    style={styles.formInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={styles.formLabel}>Amount *</label>
                  <input
                    type="number"
                    name="amount"
                    placeholder="Enter amount"
                    value={expense.amount}
                    onChange={handleChange}
                    style={styles.formInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={styles.formLabel}>GST (Optional)</label>
                  <input
                    type="number"
                    name="gst"
                    placeholder="Enter GST percentage"
                    value={expense.gst}
                    onChange={handleChange}
                    style={styles.formInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={styles.formLabel}>GST Amount</label>
                  <input
                    type="number"
                    name="gstAmount"
                    placeholder="GST Amount"
                    value={expense.gstAmount}
                    style={styles.formInputDisabled}
                    readOnly
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={styles.formLabel}>Payment Method *</label>
                  <select
                    name="method"
                    value={expense.method}
                    onChange={handleChange}
                    style={styles.formSelect}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  >
                    <option value="">Select Payment Method</option>
                    <option value="cash">Cash</option>
                    <option value="online">Online</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={addOrUpdateExpense}
                    style={styles.addButton}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                    }}
                  >
                    {editingExpenseId ? 'Update Expense' : '+ Add Expense'}
                  </button>
                  {editingExpenseId && (
                    <button
                      onClick={cancelEdit}
                      style={styles.cancelButton}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(107, 114, 128, 0.3)';
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
            {filteredExpenses.length > 0 ? (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>Sr.No</th>
                    <th style={styles.tableHeader}>Expense Type</th>
                    <th style={styles.tableHeader}>Amount</th>
                    <th style={styles.tableHeader}>GST (%)</th>
                    <th style={styles.tableHeader}>GST Amount</th>
                    <th style={styles.tableHeader}>Payment Method</th>
                    <th style={styles.tableHeader}>Date</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((exp, index) => (
                    <tr
                      key={exp.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                      <td style={styles.tableCell}>{exp.type}</td>
                      <td style={styles.tableCell}>{exp.amount.toFixed(2)}</td>
                      <td style={styles.tableCell}>{(exp.gst || 0).toFixed(2)}</td>
                      <td style={styles.tableCell}>{(exp.gstAmount || 0).toFixed(2)}</td>
                      <td style={styles.tableCell}>{exp.method}</td>
                      <td style={styles.tableCell}>
                        {new Date(exp.createdAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit'
                        })}
                      </td>
                      <td style={styles.actionCell}>
                        <button
                          onClick={() => editExpense(exp)}
                          style={{ ...styles.actionButton, ...styles.editButton }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#059669';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#10b981';
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteExpense(exp.id)}
                          style={{ ...styles.actionButton, ...styles.deleteButton }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dc2626';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#ef4444';
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={styles.noData}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>💸</div>
                <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Expenses Found</h3>
                <p style={{ marginBottom: '1.5rem' }}>
                  {reportFilter !== 'all' 
                    ? `No expenses found for {reportFilter} view. Try changing the report filter.`
                    : 'Get started by adding your first expense!'}
                </p>
                <button
                  onClick={() => document.querySelector('input[name="type"]').focus()}
                  style={{
                    ...styles.addButton,
                    fontSize: '1rem',
                    padding: '1rem 2rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                  }}
                >
                  + Add Your First Expense
                </button>
              </div>
            )}
            <div style={styles.balanceContainer}>
              Balance ({reportFilter === 'all' ? 'Total' : reportFilter === 'monthly' ? 'This Month' : 'This Year'}): {filteredBalance.toFixed(2)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DailyExpensePage;